import { FC, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { DeviceEvent } from "../../../api/types/Camera";
import { ListingResponse } from "../../../api/types/Listing";
import svgIcons from "../../../assets/svg/svgIcons";
import CameraVideoHistoryEventsMap from "../../camera-admin/video-history/video-history-events-map";
import { IncidentDetailResponse } from "./type";
import { APP_CONFIG } from "../../../constants/constants";

type IncidentDetailGpsMapProps = {
  incident: IncidentDetailResponse;
  printMode?: boolean;
  onLoadComplete?: () => void;
};

const IncidentDetailGpsMap: FC<IncidentDetailGpsMapProps> = ({ incident, printMode, onLoadComplete }) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "admins.incident.incident_review.detailsPage.widget_gps_map",
  });
  const hasCalledLoadComplete = useRef(false);

  const dataDeviceEventsList: ListingResponse<DeviceEvent[]> | undefined =
    useMemo(() => {
      if (!incident?.latitude || !incident?.longitude) return undefined;
      const syntheticEvent: DeviceEvent = {
        accountid: "",
        address: incident.address,
        altitude: incident.altitude,
        attachtype: "",
        cameraid: incident.camera_id || "",
        creationtime: incident.timestamp,
        deviceid: incident.deviceid,
        displayname: incident.asset_name,
        device_description: incident.asset_name,
        driverid: incident.driverid,
        drivername: incident.driver_name,
        gpsage: incident.gpsage,
        heading: incident.heading,
        ignitionstate: incident.ignitionstate,
        latitude: incident.latitude,
        longitude: incident.longitude,
        recordid: incident.recordid,
        rfidtag: "",
        speedkph: incident.speedkph,
        speedlimitkph: incident.speedlimitkph,
        status: { description: incident.incident_type, statuscode: incident.statuscode },
        statuscode: incident.statuscode,
        ticket: incident.video_ticket || null,
        timestamp: incident.timestamp,
      };
      return { count: 1, next: "", previous: "", results: [syntheticEvent] };
    }, [incident]);


  return (
    <div className="flex flex-col bg-white rounded-xl border border-heading-gray888/20 shadow-sm p-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className={`font-semibold text-heading-black ${printMode ? "text-xl" : "text-[15px]"}`}>{t("heading")}</h2>
        <div className="flex items-center gap-2">
          <span className="no-print flex items-center justify-center size-7 rounded-full hover:bg-accent-blue-pale transition cursor-pointer">
            <img src={svgIcons.ExpandWindowIcon} alt="" className="size-4 opacity-50" />
          </span>
        </div>
      </div>
      <div className={`flex-1 min-h-52 rounded-lg ${printMode ? "overflow-visible" : "overflow-hidden"}`}>
        <div className={printMode ? "h-[420px] w-full print:h-[420px]" : "h-full w-full"}>
          <CameraVideoHistoryEventsMap
            compact={printMode? false:true}
            showAltitude={true}
            dataDeviceEventsList={dataDeviceEventsList}
            isFetchingDeviceEventsList={false}
            onLoadComplete={() => {
              setTimeout(() => {
                if (!hasCalledLoadComplete.current) {
                  onLoadComplete?.();
                  if (APP_CONFIG.DEBUG.ALL)
                    console.log(
                      "===== INCIDENT GPS TAB LOADED",
                      new Date().getTime()
                    );
                  hasCalledLoadComplete.current = true;
                }
              }, 100);
            }}
            printMode={printMode}
          />
        </div>
      </div>
    </div>
  );
};

export default IncidentDetailGpsMap;
